import LaunchIcon from '@mui/icons-material/Launch';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import {
    Box,
    CircularProgress,
    Fade,
    IconButton,
    Modal,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
    alpha,
} from '@mui/material';
import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useNavigate } from 'react-router-dom';
import { useFormatter } from '../../context/FormatterContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import { getDisplayCoin } from '../../utils/displayFormatters';

interface AssetDistributionModalProps {
    open: boolean;
    onClose: () => void;
    assetName: string;
    assetSymbol: string;
    assetTicker: string;
}

const AssetDistributionModal: React.FC<AssetDistributionModalProps> = ({
    open,
    onClose,
    assetName,
    assetSymbol,
    assetTicker,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { formatPercentage, formatSmartQuantity, formatSmartPrice, formatSmartCurrency, formatWithTooltip } =
        useFormatter();
    const { assetDistribution, isAssetDistributionLoading, loadAssetDistribution } = useDashboardData();

    const handleNavigateToTransactions = (accountName: string) => {
        onClose();
        navigate(`/transactions?asset=${assetSymbol}&account=${accountName}`);
    };

    // Handle modal entrance - load asset distribution on open
    const handleModalEnter = () => {
        if (assetSymbol) {
            loadAssetDistribution(assetSymbol);
        }
    };

    return (
        <Modal 
            open={open} 
            onClose={onClose} 
            onTransitionEnter={handleModalEnter} 
            closeAfterTransition
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(5px)'
                }
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
                        maxWidth: '900px',
                        maxHeight: { xs: '95vh', sm: '90vh' },
                        overflow: 'auto',
                        bgcolor: (theme) => theme.palette.background.paper,
                        color: (theme) => theme.palette.text.primary,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: (theme) => theme.palette.divider,
                        boxShadow: (theme) => `0px 5px 15px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                        p: { xs: 2, sm: 3, md: 4 },
                    }}
                >
                    <Typography variant="h6" component="h2" gutterBottom sx={{ 
                        fontSize: { xs: '1rem', sm: '1.1rem' }, 
                        fontWeight: 700,
                        mb: { xs: 1, sm: 2 }
                    }}>
                        {t('dashboard.assetDistribution.title', {
                            assetName,
                            symbol: getDisplayCoin(assetTicker, assetSymbol)
                        })}
                    </Typography>
                    {isAssetDistributionLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} minHeight={200}>
                            <CircularProgress />
                        </Box>
                    ) : !assetDistribution?.length ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} minHeight={200}>
                            <Typography color="text.secondary">{t('dashboard.assetDistribution.noData')}</Typography>
                        </Box>
                    ) : (
                        <TableContainer
                            sx={{
                                mt: 2,
                                maxHeight: '100%',
                                overflow: 'auto',
                                px: 0,
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                    height: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: 'transparent',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: alpha(theme.palette.divider, 0.6),
                                    borderRadius: '4px',
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                },
                            }}
                        >
                            <Table 
                                size="medium"
                                sx={{
                                    minWidth: { xs: 600, sm: 600, md: 650 },
                                    tableLayout: 'fixed',
                                    borderCollapse: 'collapse',
                                    width: '100%',
                                    '& .MuiTableCell-root': {
                                        py: 2,
                                        px: { xs: 1, sm: 2 },
                                        borderBottom: '2px solid',
                                        borderBottomColor: `${theme.palette.divider}`,
                                        borderBottomOpacity: 0.6,
                                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    },
                                    '& .MuiTypography-root': {
                                        fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                                    },
                                    '& .MuiTableHead-root .MuiTableCell-root': {
                                        bgcolor: 'background.paper',
                                        fontWeight: 600,
                                        borderBottom: '2px solid',
                                        borderBottomColor: `${theme.palette.divider}`,
                                    },
                                    '& tbody .MuiTableRow-root:last-child .MuiTableCell-root': {
                                        borderBottom: 'none',
                                    },
                                }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: { xs: '30%', sm: '35%', md: '40%' } }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('dashboard.assetDistribution.table.accountName')}</Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: { xs: '15%', sm: '15%', md: '15%' } }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('dashboard.assetDistribution.table.quantity')}</Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: { xs: '15%', sm: '15%', md: '15%' } }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('dashboard.assetDistribution.table.price')}</Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: { xs: '20%', sm: '20%', md: '15%' } }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('dashboard.assetDistribution.table.value')}</Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: { xs: '20%', sm: '15%', md: '15%' } }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('dashboard.assetDistribution.table.allocation')}</Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {assetDistribution.map((dist) => (
                                        <TableRow key={dist.exchangeName}>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        maxWidth: '100%',
                                                    }}>
                                                        <Tooltip title={dist.exchangeName}>
                                                            <Typography 
                                                                sx={{ 
                                                                    fontWeight: 'bold',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    maxWidth: { xs: '80px', sm: '120px', md: '150px', lg: '200px' }
                                                                }}
                                                            >
                                                                {dist.exchangeName}
                                                            </Typography>
                                                        </Tooltip>
                                                        {dist.hasBalanceDiscrepancy && (
                                                            <>
                                                                <Tooltip
                                                                    title={
                                                                        <Box sx={{ p: 1.5 }}>
                                                                            <Typography 
                                                                                variant="body2" 
                                                                                sx={{ 
                                                                                    fontWeight: 'bold', 
                                                                                    fontSize: '0.9rem',
                                                                                    whiteSpace: 'nowrap'
                                                                                }}
                                                                            >
                                                                                {t('dashboard.assetDistribution.balanceDiscrepancy.tooltip', {
                                                                                    balance: dist.imported_quantity.toString()
                                                                                })}
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    arrow
                                                                    placement="top"
                                                                    componentsProps={{
                                                                        tooltip: {
                                                                            sx: {
                                                                                maxWidth: 'none',
                                                                                width: 'auto',
                                                                                p: 0,
                                                                                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
                                                                                backdropFilter: 'blur(5px)',
                                                                                color: (theme) => theme.palette.text.primary,
                                                                                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                                                                border: '1px solid',
                                                                                borderColor: (theme) => theme.palette.divider,
                                                                                borderRadius: 1,
                                                                                fontSize: '0.9rem',
                                                                                fontWeight: 'bold',
                                                                                '& .MuiTooltip-arrow': {
                                                                                    color: (theme) => alpha(theme.palette.background.paper, 0.9),
                                                                                }
                                                                            },
                                                                        },
                                                                        popper: {
                                                                            sx: {
                                                                                '& .MuiTooltip-tooltip': {
                                                                                    p: 0,
                                                                                }
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    <IconButton
                                                                        size="small"
                                                                        color="warning"
                                                                        sx={{
                                                                        padding: '2px',
                                                                        ml: 0.5,
                                                                    }}
                                                                    >
                                                                        <ReportProblemIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip
                                                                    title={
                                                                        <Box sx={{ p: 1.5 }}>
                                                                            <Typography 
                                                                                variant="body2" 
                                                                                sx={{ 
                                                                                    fontWeight: 'bold', 
                                                                                    fontSize: '0.9rem',
                                                                                    whiteSpace: 'nowrap'
                                                                                }}
                                                                            >
                                                                                {t('dashboard.assetDistribution.balanceDiscrepancy.viewTransactions')}
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    arrow
                                                                    placement="top"
                                                                    componentsProps={{
                                                                        tooltip: {
                                                                            sx: {
                                                                                maxWidth: 'none',
                                                                                width: 'auto',
                                                                                p: 0,
                                                                                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
                                                                                backdropFilter: 'blur(5px)',
                                                                                color: (theme) => theme.palette.text.primary,
                                                                                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                                                                border: '1px solid',
                                                                                borderColor: (theme) => theme.palette.divider,
                                                                                borderRadius: 1,
                                                                                fontSize: '0.9rem',
                                                                                fontWeight: 'bold',
                                                                                '& .MuiTooltip-arrow': {
                                                                                    color: (theme) => alpha(theme.palette.background.paper, 0.9),
                                                                                }
                                                                            },
                                                                        },
                                                                        popper: {
                                                                            sx: {
                                                                                '& .MuiTooltip-tooltip': {
                                                                                    p: 0,
                                                                                }
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() =>
                                                                            handleNavigateToTransactions(
                                                                                dist.exchangeName
                                                                            )
                                                                        }
                                                                        sx={{
                                                                         padding: '2px',
                                                                        ml: 0.5,
                                                                        '&:hover': {
                                                                            backgroundColor: theme.palette.primary.light + '20',
                                                                        },
                                                                    }}
                                                                    >
                                                                        <LaunchIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title={formatWithTooltip(dist.quantity).tooltip}>
                                                    <Typography sx={{ 
                                                        fontWeight: 'bold',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {formatSmartQuantity(dist.quantity)}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title={formatWithTooltip(dist.unit_price).tooltip}>
                                                    <Typography sx={{ 
                                                        fontWeight: 'bold',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {formatSmartPrice(dist.unit_price)}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title={formatWithTooltip(dist.value).tooltip}>
                                                    <Typography sx={{ 
                                                        fontWeight: 'bold',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {formatSmartCurrency(dist.value)}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography sx={{ 
                                                    fontWeight: 'bold',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {formatPercentage(dist.allocationPercentage)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
};

export default AssetDistributionModal;
